/**
 * /field/downhole/assemblies/:id/teardown — the disassembly checklist
 * (fieldiq-api#905 `POST /assemblies/:id/disassemble`).
 *
 * ONE LINE PER TOOL STILL IN THE STRING. The API refuses a checklist that
 * misses a tool or names one twice, and a tool lost in hole has no line — it
 * never came back. The screen builds the lines from the assembly, so it cannot
 * send either mistake; the lost tools are listed, without inputs, so the hand
 * sees why they are missing.
 *
 * THE ROUTING RULES ARE THE API'S, mirrored so nothing offered is refused:
 *   return_to_stock   only a good or serviceable tool, and only with dope &
 *                     cleaning confirmed. The tool goes back to Available.
 *   inspect / repair  the tool goes to the shop and a work order opens on it.
 *   scrap             retired, serial kept. A scrap condition can only scrap.
 * There is no default condition or action. A checklist line nobody filled in
 * is not "good / return to stock", and pre-filling it would send every tool
 * back to the shelf on one tap.
 *
 * OD MEASURED is typed in the tenant's units and sent in inches (three places,
 * the column's scale). It is the only measure on the form.
 *
 * Irreversible: the assembly becomes disassembled and the tools move. So the
 * submit asks once more, and the answer shows what happened to each tool, with
 * the work orders it opened.
 *
 * ONE CHECKLIST, TWO SHELLS. `TeardownChecklist` is the form; this screen and
 * the office's /downhole/assemblies/:id/teardown (TeardownScreen) each wrap it
 * in their own page and pass where its links go, so the office and the yard
 * cannot drift apart on what a teardown sends.
 *
 * THE WRAPPER HOLDS THE ANSWER, AND CHECKS IT BEFORE THE STATUS. A teardown
 * re-reads every downhole query, so the assembly comes straight back
 * `disassembled` — and a wrapper that tested the status first would swap the
 * checklist (and the answer it was holding) for "only a string back from the
 * well can be torn down" the moment the save landed.
 */
import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../../api/client';
import { useAuth } from '../../../contexts/AuthContext';
import { useDisassemble, useDownholeAssembly } from '../../../hooks/useDownhole';
import { onlineOrSay } from '../../../lib/downhole-offline';
import { canBuildDownhole } from '../../../lib/downhole-roles';
import {
  DH_ASSEMBLY_STATUS_SKIN,
  DH_CATEGORY_LABEL,
  DH_COMPONENT_STATUS_SKIN,
  DH_CONDITION_LABEL,
  DH_DISASSEMBLY_REASON_LABEL,
  DH_RECOMMEND_ACTION_LABEL,
  dhLabel,
  dhSkin,
} from '../../../lib/downhole-labels';
import { dhUnitLabel, toStoredImperial } from '../../../lib/downhole-units';
import {
  DH_DISASSEMBLY_REASONS,
  DH_ITEM_CONDITIONS,
  DH_RECOMMEND_ACTIONS,
  type DhAssemblyDetail,
  type DhAssemblyMember,
  type DhDisassembleItemWrite,
  type DhDisassemblyResult,
  type DhUnitSystem,
} from '../../../types/downhole';
import { DH_INPUT, DH_SECONDARY, DhBadge, DhDialog, DhField } from '../DownholeBits';
import { FIELD_PRIMARY, FIELD_SECONDARY, FieldCard, FieldEmpty, FieldQueryState, FieldSectionTitle, FieldTitle } from './FieldBits';

interface Line {
  condition: string;
  action: string;
  dope: boolean;
  box: string;
  pin: string;
  od: string;
  wear: string;
  damage: string;
}
const BLANK: Line = { condition: '', action: '', dope: false, box: '', pin: '', od: '', wear: '', damage: '' };

/** Which actions the API accepts for a condition. */
export function actionsFor(condition: string): string[] {
  if (condition === 'scrap') return ['scrap'];
  if (condition === 'good' || condition === 'serviceable') return [...DH_RECOMMEND_ACTIONS];
  return DH_RECOMMEND_ACTIONS.filter((a) => a !== 'return_to_stock');
}

/** A line's problems, in the API's terms; empty when it can be sent. */
export function lineProblems(serial: string, l: Line, units: DhUnitSystem | null): string[] {
  const out: string[] = [];
  if (!l.condition) out.push(`${serial}: choose its condition`);
  if (!l.action) out.push(`${serial}: choose what happens to it`);
  if (l.condition && l.action && !actionsFor(l.condition).includes(l.action)) {
    out.push(`${serial}: a ${dhLabel(DH_CONDITION_LABEL, l.condition).toLowerCase()} tool cannot be sent to ${dhLabel(DH_RECOMMEND_ACTION_LABEL, l.action).toLowerCase()}`);
  }
  if (l.action === 'return_to_stock' && !l.dope) out.push(`${serial}: confirm dope & cleaning before it goes back to stock`);
  if (l.od.trim() && toStoredImperial(l.od, 'diameter', units) === null) out.push(`${serial}: OD measured must be a number`);
  return out;
}

export default function FieldTeardownScreen() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const query = useDownholeAssembly(id);
  const asm = query.data?.data ?? null;
  const units = query.data?.meta.unit_system ?? null;
  const [result, setResult] = useState<DhDisassemblyResult | null>(null);
  const links: TeardownLinks = {
    back: `/field/downhole/assemblies/${id}`,
    backLabel: 'Back to the assembly',
    workOrder: (woId) => `/field/downhole/work/${woId}`,
    done: '/field/downhole',
    doneLabel: 'Back to assemblies',
  };

  if (result && asm) return <TeardownResult result={result} assemblyNumber={asm.assembly_number} links={links} />;

  return (
    <div data-testid="downhole-field-teardown">
      <FieldQueryState query={query} subject="this assembly">
        {!asm ? (
          <FieldEmpty>This assembly does not exist in your company.</FieldEmpty>
        ) : !canBuildDownhole(user?.role) ? (
          <FieldEmpty>Your role cannot tear down an assembly.</FieldEmpty>
        ) : asm.status !== 'laid_down' ? (
          <FieldEmpty>
            {asm.assembly_number} is {dhSkin(DH_ASSEMBLY_STATUS_SKIN, asm.status).label.toLowerCase()}. Only a string back from the well can be torn down.
          </FieldEmpty>
        ) : (
          <TeardownChecklist
            asm={asm}
            units={units}
            links={links}
            onDone={setResult}
            header={
              <FieldTitle eyebrow={`Tear down · ${asm.assembly_number}`} title={asm.name}>
                <p className="mt-1 text-sm text-fieldiq-text">
                  One line per tool. Every tool needs a condition and what happens to it next.
                </p>
              </FieldTitle>
            }
          />
        )}
      </FieldQueryState>
    </div>
  );
}

export interface TeardownLinks {
  back: string;
  backLabel: string;
  workOrder: (workOrderId: string) => string;
  done: string;
  doneLabel: string;
}

/**
 * The checklist itself, for a laid_down assembly the caller has already
 * checked (status and role). It hands the API's answer to `onDone`, and the
 * caller renders `TeardownResult` from it (see the docblock above for why the
 * caller, not this). `wide` lays the tool cards two across for the office's
 * desktop page; the field shell stacks them.
 */
export function TeardownChecklist({
  asm,
  units,
  links,
  onDone,
  header,
  wide = false,
}: {
  asm: DhAssemblyDetail;
  units: DhUnitSystem | null;
  links: TeardownLinks;
  onDone: (result: DhDisassemblyResult) => void;
  header?: React.ReactNode;
  wide?: boolean;
}) {
  const disassemble = useDisassemble();
  const [reason, setReason] = useState('');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<Record<string, Line>>({});
  const [confirming, setConfirming] = useState(false);

  const { returned, lost } = useMemo(() => {
    const members: DhAssemblyMember[] = asm?.components ?? [];
    return {
      returned: members.filter((m) => m.condition_on_return !== 'lost_in_hole'),
      lost: members.filter((m) => m.condition_on_return === 'lost_in_hole'),
    };
  }, [asm]);

  const line = (cid: string) => lines[cid] ?? BLANK;
  const setLine = (cid: string, patch: Partial<Line>) =>
    setLines((all) => {
      const next = { ...(all[cid] ?? BLANK), ...patch };
      // A condition change that rules out the chosen action clears it, rather
      // than leaving a pair the API would refuse sitting on screen.
      if (patch.condition !== undefined && next.action && !actionsFor(next.condition).includes(next.action)) next.action = '';
      return { ...all, [cid]: next };
    });

  const problems = (): string[] => {
    const out: string[] = [];
    if (!reason) out.push('Choose why the string came out');
    for (const m of returned) out.push(...lineProblems(m.serial_number, line(m.component_id), units));
    return out;
  };

  const review = () => {
    const p = problems();
    if (p.length) {
      toast.error(p.slice(0, 3).join('. ') + (p.length > 3 ? ` (and ${p.length - 3} more)` : ''));
      return;
    }
    setConfirming(true);
  };

  const submit = async () => {
    if (!asm || !onlineOrSay(toast.error)) return;
    const items: DhDisassembleItemWrite[] = returned.map((m) => {
      const l = line(m.component_id);
      const item: DhDisassembleItemWrite = { component_id: m.component_id, condition: l.condition, recommend_action: l.action };
      if (l.dope) item.dope_clean_confirmed = true;
      if (l.box.trim()) item.box_thread_condition = l.box.trim();
      if (l.pin.trim()) item.pin_thread_condition = l.pin.trim();
      if (l.wear.trim()) item.wear_notes = l.wear.trim();
      if (l.damage.trim()) item.damage_notes = l.damage.trim();
      const od = toStoredImperial(l.od, 'diameter', units);
      if (od !== null) item.od_measurement = od;
      return item;
    });
    try {
      const res = await disassemble.mutateAsync({
        id: asm.id,
        body: { reason, ...(location.trim() && { location: location.trim() }), ...(notes.trim() && { notes: notes.trim() }), items },
      });
      setConfirming(false);
      toast.success(`${asm.assembly_number} torn down`);
      if (res.data) onDone(res.data);
    } catch (error) {
      setConfirming(false);
      toast.error(getApiErrorMessage(error, 'The teardown was not saved.'));
    }
  };

  return (
    <>
      {header}

      <FieldCard className="space-y-4 p-4">
        <DhField label="Why it came out *">
          <select className={DH_INPUT} value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="">Choose…</option>
            {DH_DISASSEMBLY_REASONS.map((r) => (
              <option key={r} value={r}>
                {DH_DISASSEMBLY_REASON_LABEL[r]}
              </option>
            ))}
          </select>
        </DhField>
        <DhField label="Where (yard, shop, rig)">
          <input className={DH_INPUT} value={location} onChange={(e) => setLocation(e.target.value)} maxLength={100} />
        </DhField>
      </FieldCard>

      <FieldSectionTitle>Tools · {returned.length}</FieldSectionTitle>
      <div className={wide ? 'grid gap-3 lg:grid-cols-2' : 'space-y-3'}>
        {returned.map((m) => {
          const l = line(m.component_id);
          const allowed = l.condition ? actionsFor(l.condition) : [...DH_RECOMMEND_ACTIONS];
          return (
            <FieldCard key={m.component_id} className="space-y-3 p-4">
              <div>
                <p className="font-mono text-sm font-extrabold text-fieldiq-accent-signal">
                  {m.position}. {m.serial_number}
                </p>
                <p className="text-xs text-fieldiq-text">{dhLabel(DH_CATEGORY_LABEL, m.category)}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <DhField label="Condition *">
                  <select
                    className={DH_INPUT}
                    aria-label={`Condition of ${m.serial_number}`}
                    value={l.condition}
                    onChange={(e) => setLine(m.component_id, { condition: e.target.value })}
                  >
                    <option value="">Choose…</option>
                    {DH_ITEM_CONDITIONS.map((c) => (
                      <option key={c} value={c}>
                        {DH_CONDITION_LABEL[c]}
                      </option>
                    ))}
                  </select>
                </DhField>
                <DhField label="Next *">
                  <select
                    className={DH_INPUT}
                    aria-label={`Next for ${m.serial_number}`}
                    value={l.action}
                    onChange={(e) => setLine(m.component_id, { action: e.target.value })}
                  >
                    <option value="">Choose…</option>
                    {allowed.map((a) => (
                      <option key={a} value={a}>
                        {DH_RECOMMEND_ACTION_LABEL[a]}
                      </option>
                    ))}
                  </select>
                </DhField>
              </div>
              <label className="flex min-h-[2.75rem] items-center gap-3 text-sm text-fieldiq-text">
                <input
                  type="checkbox"
                  className="size-5"
                  checked={l.dope}
                  onChange={(e) => setLine(m.component_id, { dope: e.target.checked })}
                  aria-label={`Dope and cleaning done on ${m.serial_number}`}
                />
                Dope &amp; cleaning done{l.action === 'return_to_stock' ? ' (required to return to stock)' : ''}
              </label>
              <details className="text-sm">
                <summary className="cursor-pointer py-1 font-bold text-fieldiq-text">Threads, OD and notes</summary>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <DhField label="Box thread">
                    <input className={DH_INPUT} value={l.box} maxLength={50} onChange={(e) => setLine(m.component_id, { box: e.target.value })} />
                  </DhField>
                  <DhField label="Pin thread">
                    <input className={DH_INPUT} value={l.pin} maxLength={50} onChange={(e) => setLine(m.component_id, { pin: e.target.value })} />
                  </DhField>
                  <DhField label={`OD measured (${dhUnitLabel('diameter', units)})`}>
                    <input className={DH_INPUT} inputMode="decimal" value={l.od} onChange={(e) => setLine(m.component_id, { od: e.target.value })} />
                  </DhField>
                  <div className="col-span-2">
                    <DhField label="Wear notes">
                      <textarea className={`${DH_INPUT} h-20 py-2`} value={l.wear} maxLength={10000} onChange={(e) => setLine(m.component_id, { wear: e.target.value })} />
                    </DhField>
                  </div>
                  <div className="col-span-2">
                    <DhField label="Damage notes">
                      <textarea className={`${DH_INPUT} h-20 py-2`} value={l.damage} maxLength={10000} onChange={(e) => setLine(m.component_id, { damage: e.target.value })} />
                    </DhField>
                  </div>
                </div>
              </details>
            </FieldCard>
          );
        })}
      </div>

      {lost.length > 0 && (
        <>
          <FieldSectionTitle>Lost in hole · {lost.length}</FieldSectionTitle>
          <FieldCard>
            <ul className="divide-y divide-fieldiq-border">
              {lost.map((m) => (
                <li key={m.component_id} className="px-4 py-3 text-sm text-fieldiq-text">
                  <span className="font-mono font-bold">{m.serial_number}</span> did not come back, so it has no checklist line.
                </li>
              ))}
            </ul>
          </FieldCard>
        </>
      )}

      <FieldCard className="mt-4 p-4">
        <DhField label="Notes">
          <textarea className={`${DH_INPUT} h-20 py-2`} value={notes} maxLength={10000} onChange={(e) => setNotes(e.target.value)} />
        </DhField>
      </FieldCard>

      <div className="mt-4 space-y-2">
        <button type="button" className={FIELD_PRIMARY} onClick={review} disabled={disassemble.isPending}>
          Finish teardown
        </button>
        <Link to={links.back} className={FIELD_SECONDARY}>
          {links.backLabel}
        </Link>
      </div>

      <DhDialog
        open={confirming}
        title={`Tear down ${asm.assembly_number}?`}
        onClose={() => setConfirming(false)}
        footer={
          <>
            <button type="button" className={DH_SECONDARY} onClick={() => setConfirming(false)}>
              Keep editing
            </button>
            <button
              type="button"
              className="fi-action fi-action--standard fi-action--primary disabled:opacity-50"
              disabled={disassemble.isPending}
              onClick={submit}
            >
              {disassemble.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
              Tear down
            </button>
          </>
        }
      >
        <ConfirmSummary returned={returned} lines={lines} />
      </DhDialog>
    </>
  );
}

function ConfirmSummary({ returned, lines }: { returned: DhAssemblyMember[]; lines: Record<string, Line> }) {
  const tally = (action: string) => returned.filter((m) => lines[m.component_id]?.action === action).length;
  return (
    <div className="space-y-2 text-sm text-fieldiq-text">
      <p>This cannot be undone. The string is taken apart and each tool moves:</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>{tally('return_to_stock')} back to stock</li>
        <li>{tally('inspect') + tally('repair')} to the shop, each with a work order</li>
        <li>{tally('scrap')} scrapped (retired, serial kept)</li>
      </ul>
    </div>
  );
}

export function TeardownResult({ result, assemblyNumber, links }: { result: DhDisassemblyResult; assemblyNumber: string; links: TeardownLinks }) {
  return (
    <div data-testid="downhole-field-teardown-result">
      <FieldTitle eyebrow="Teardown saved" title={assemblyNumber}>
        <p className="mt-1 flex items-center gap-2 text-sm text-fieldiq-text">
          <CheckCircle2 className="size-4" aria-hidden="true" /> Each tool is where the checklist sent it.
        </p>
      </FieldTitle>
      <FieldCard>
        <ul className="divide-y divide-fieldiq-border">
          {result.items.map((i) => (
            <li key={i.id} className="px-4 py-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono font-extrabold text-fieldiq-accent-signal">{i.serial_number}</span>
                <DhBadge map={DH_COMPONENT_STATUS_SKIN} value={i.component_status} />
              </div>
              <p className="mt-0.5 text-xs text-fieldiq-text">
                {dhLabel(DH_CONDITION_LABEL, i.condition)} · {dhLabel(DH_RECOMMEND_ACTION_LABEL, i.recommend_action)}
                {i.work_order_id && (
                  <>
                    {' · '}
                    <Link to={links.workOrder(i.work_order_id)} className="font-bold text-fieldiq-accent-signal underline">
                      {i.work_order_number ?? 'Work order'}
                    </Link>
                  </>
                )}
              </p>
            </li>
          ))}
        </ul>
      </FieldCard>
      <div className="mt-4">
        <Link to={links.done} className={FIELD_SECONDARY}>
          {links.doneLabel}
        </Link>
      </div>
    </div>
  );
}
